import { useMemo, useState, type ReactNode } from 'react';
import { TooltipProvider } from '@monksflow/monks-ui';
import { LanguagesIcon, PencilLineIcon, UsersIcon } from 'lucide-react';
import {
  buildCustomizationFormValues,
  EDITABLE_SPRINGBOARD_CATEGORY_IDS,
  useSpringboardCategoryCustomization,
  type EditableSpringboardCategoryId,
} from '@/features/springboard';
import { useTranslation } from 'react-i18next';
import { AdminPageShell } from '../AdminPageShell';
import { CategoryCard, CategoryCardSkeleton } from './CategoryCard';
import { CategoryEditSheet } from './CategoryEditSheet';

/** The page's title, body card and About dialog, bound once for all three states. */
function CustomizationShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('admin');

  return (
    <AdminPageShell
      title={t('admin.customization.categoriesCustomization')}
      about={{
        title: t('admin.customization.about.title'),
        description: t('admin.customization.about.description'),
        rows: [
          { icon: PencilLineIcon, text: t('admin.customization.about.renameEachCategory') },
          { icon: LanguagesIcon, text: t('admin.customization.about.emptyFieldsUseDefault') },
          { icon: UsersIcon, text: t('admin.customization.about.sharedAcrossOrganization') },
        ],
      }}
    >
      {children}
    </AdminPageShell>
  );
}

export const CustomizationPage = () => {
  const { t } = useTranslation('admin');
  const { data, isLoading, isError } = useSpringboardCategoryCustomization();
  const categories = useMemo(() => buildCustomizationFormValues(data).categories, [data]);

  // `key` remounts the sheet on every open, so it seeds from what is stored now; the id
  // outlives the close so the exit animation plays over the category it showed.
  const [editing, setEditing] = useState<{
    id: EditableSpringboardCategoryId;
    open: boolean;
    key: number;
  } | null>(null);

  if (isLoading) {
    return (
      <CustomizationShell>
        <div className="grid grid-cols-1 gap-sm md:grid-cols-2" aria-busy>
          {EDITABLE_SPRINGBOARD_CATEGORY_IDS.map((id) => (
            <CategoryCardSkeleton key={id} />
          ))}
        </div>
      </CustomizationShell>
    );
  }

  if (isError) {
    return (
      <CustomizationShell>
        <p className="text-sm text-destructive">
          {t('admin.customization.failedToLoadCustomizationSettings')}
        </p>
      </CustomizationShell>
    );
  }

  return (
    <CustomizationShell>
      <TooltipProvider>
        <div className="grid grid-cols-1 gap-sm md:grid-cols-2">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category}
              onEdit={() =>
                setEditing((prev) => ({ id: category.id, open: true, key: (prev?.key ?? 0) + 1 }))
              }
            />
          ))}
        </div>
      </TooltipProvider>

      {editing && (
        <CategoryEditSheet
          key={editing.key}
          open={editing.open}
          categories={categories}
          categoryId={editing.id}
          onClose={() => setEditing((prev) => prev && { ...prev, open: false })}
        />
      )}
    </CustomizationShell>
  );
};
